import request, { checkBlockPopup } from '@/api-v2/request';
import type { IRegressionHeatmapQuery } from './types/IRegressionHeatmapQuery';
import type { IRegressionHeatmapResponseItem } from './types/IRegressionHeatmapResponseItem';
import type { IRegressionResponse } from './types/IRegressionResponse';
import type { PartialRegressionSetDTO } from './types/IRegressionSetDTO';
import type { RegressionSetResponse } from './types/RegressionSetResponse';
import type {
  ExpertRegressionCalcRequest,
  IRegressionCalcRequest,
  IRegressionCalcRequestByRegressionId,
} from './types/IRegressionCalcRequest';
import type { RegressionDateRequest } from './types/RegressionDateRequest';
import type { FileSignedUrlDTO } from '@/types/dto/FileSignedUrlDTO';
import { FileService } from '@/api-v2/web/file';
import { RouteName } from '@/constants/RouteName';
import { useDefaultPdfOptions } from '@/composables/usePdf';
import { Locales } from '@/constants/Locales';
import type {
  IKdeRegressionCalcResponse,
  IQQPlotRegressionCalcResponse,
  IRegressionCalcResponse,
} from './types/IRegressionCalcResponse';
import type { RegressionSummaryRequest } from './types/RegressionSummaryRequest';
import type { RegressionSummaryResponse } from './types/RegressionSummaryResponse';
import type { FactorDecompositionPdfParameters } from './types/FactorDecompositionPdfParams';
import type { IExpertRegressionResponse } from './types/IExpertRegressionResponse';
import type { FactorComparisonRegressionCalcResponse } from './types/FactorComparisonRegressionCalcResponse';
import type { FactorComparisonRegressionCalcRequest } from './types/FactorComparisonRegressionCalcRequest';
import type { PortfolioVersionType } from '@/constants/PortfolioVersionType';

const defaultPdfOptions = useDefaultPdfOptions();

const getShouldUseAlgoQuantHeaders = (shouldUseAlgoQuant?: boolean): { 'Should-Use-AlgoQuant': string } | undefined => {
  if (shouldUseAlgoQuant === undefined) return undefined;

  return { 'Should-Use-AlgoQuant': String(shouldUseAlgoQuant) };
};

/**
 * @returns all the regressions for the user's team
 */
export const getAllTeamRegressions = (signal: AbortSignal): Promise<RegressionSetResponse[]> => {
  return request({
    url: `/regression`,
    method: 'GET',
    signal,
  }).then((o): RegressionSetResponse[] => o.data);
};

/**
 * @returns returns all linked regressions for a particular slug
 *
 * Use this on regression page load to minimize the amount of data loaded on the page,
 * but use getAllTeamRegressions to populate the ImportFactorSetsModal so that you have
 * all possible ones.
 */
export const getAllRegressionsForTimeseries = (slug: string, signal: AbortSignal): Promise<IRegressionResponse[]> => {
  return request({
    url: `/regression/${slug}`,
    method: 'GET',
    signal,
  }).then((o): IRegressionResponse[] => o.data);
};

/**
 *
 * @param slug This will either be the master portfolio tree slug if it's something in a portfolio tree,
 * and the strategy code if it's just the strategy
 */
export const getEarliestAnalysisDateForRegression = (
  slug: string,
  payload: RegressionDateRequest,
  signal: AbortSignal,
): Promise<string> =>
  request({
    url: `/regression/${slug}/date`,
    method: 'POST',
    data: payload,
    signal,
  }).then((o): string => o.data);

export const postRegression = (slug: string, payload: PartialRegressionSetDTO): Promise<IRegressionResponse> =>
  request({
    url: `/regression/${slug}`,
    method: 'POST',
    data: payload,
  }).then((o): IRegressionResponse => o.data);

export const putRegression = (
  slug: string,
  attributeId: string,
  payload: PartialRegressionSetDTO,
): Promise<IRegressionResponse> =>
  request({
    url: `/regression/${slug}`,
    method: 'PUT',
    params: {
      attributeId,
    },
    data: payload,
  }).then((o): IRegressionResponse => o.data);

export const deleteRegression = (slug: string, attributeId: string): Promise<IRegressionResponse> =>
  request({
    url: `/regression/${slug}`,
    method: 'DELETE',
    params: {
      attributeId,
    },
  }).then((o): IRegressionResponse => o.data);

/**
 * mainRegresseeId is used in the calculation of the regressions.
 * It is used in the url for the api itself as the slug
 * If it's a portfolio tree, then we supply the portfolio slug.
 * If it's a strategy, then we supply the strategy code
 */
export const calcRegression = (
  mainRegresseeId: string,
  data: IRegressionCalcRequest,
  signal?: AbortSignal,
  shouldUseAlgoQuant?: boolean,
): Promise<IRegressionCalcResponse> =>
  request({
    url: `/regression/${mainRegresseeId}/result`,
    method: 'POST',
    data,
    signal,
    headers: getShouldUseAlgoQuantHeaders(shouldUseAlgoQuant),
  }).then((o): IRegressionCalcResponse => o.data);

export const calcComparisonRegression = (
  mainRegresseeId: string,
  data: FactorComparisonRegressionCalcRequest,
  signal?: AbortSignal,
): Promise<FactorComparisonRegressionCalcResponse> =>
  request({
    url: `/regression/${mainRegresseeId}/comparison-result`,
    method: 'POST',
    data,
    signal,
  }).then((o): FactorComparisonRegressionCalcResponse => o.data);

export const calcKdeRegression = (
  mainRegresseeId: string,
  data: IRegressionCalcRequest,
  signal: AbortSignal,
  shouldUseAlgoQuant?: boolean,
): Promise<IKdeRegressionCalcResponse> =>
  request({
    url: `/regression/${mainRegresseeId}/kde`,
    method: 'POST',
    data,
    signal,
    headers: getShouldUseAlgoQuantHeaders(shouldUseAlgoQuant),
  }).then((o): IKdeRegressionCalcResponse => o.data);

export const calcQQPlotRegression = (
  mainRegresseeId: string,
  data: IRegressionCalcRequest,
  signal: AbortSignal,
  shouldUseAlgoQuant?: boolean,
): Promise<IQQPlotRegressionCalcResponse> =>
  request({
    url: `/regression/${mainRegresseeId}/qqplot`,
    method: 'POST',
    data,
    signal,
    headers: getShouldUseAlgoQuantHeaders(shouldUseAlgoQuant),
  }).then((o): IQQPlotRegressionCalcResponse => o.data);

export const calcRegressionSummary = (
  mainRegresseeId: string,
  data: RegressionSummaryRequest,
  signal: AbortSignal,
): Promise<RegressionSummaryResponse> =>
  request({
    url: `/regression/${mainRegresseeId}/summary`,
    method: 'POST',
    data,
    signal,
  }).then((o): RegressionSummaryResponse => o.data);

export const calcRegressionById = (
  slug: string,
  data: IRegressionCalcRequestByRegressionId,
  signal?: AbortSignal,
  shouldUseAlgoQuant?: boolean,
): Promise<IRegressionCalcResponse> =>
  request({
    url: `/regression/${slug}/resultById`,
    method: 'POST',
    data,
    signal,
    headers: getShouldUseAlgoQuantHeaders(shouldUseAlgoQuant),
  }).then((o): IRegressionCalcResponse => o.data);

export const calcKdeRegressionById = (
  slug: string,
  data: IRegressionCalcRequestByRegressionId,
  signal?: AbortSignal,
  shouldUseAlgoQuant?: boolean,
): Promise<IKdeRegressionCalcResponse> =>
  request({
    url: `/regression/${slug}/kdeById`,
    method: 'POST',
    data,
    signal,
    headers: getShouldUseAlgoQuantHeaders(shouldUseAlgoQuant),
  }).then((o): IKdeRegressionCalcResponse => o.data);

export const calcQQPlotRegressionById = (
  slug: string,
  data: IRegressionCalcRequestByRegressionId,
  signal?: AbortSignal,
  shouldUseAlgoQuant?: boolean,
): Promise<IQQPlotRegressionCalcResponse> =>
  request({
    url: `/regression/${slug}/qqplotById`,
    method: 'POST',
    data,
    signal,
    headers: getShouldUseAlgoQuantHeaders(shouldUseAlgoQuant),
  }).then((o): IQQPlotRegressionCalcResponse => o.data);

/**
 * @param slug - this is always either the portfolio slug or the CODE of the itemUnderAnalysis,
 * which is a marked difference from the other regression APIs where we use the portfolioTreeId
 * for the itemUnderAnalysis
 */
export const getPortfolioTreeHeatmap = (
  slug: string,
  payload: IRegressionHeatmapQuery,
  signal: AbortSignal,
): Promise<IRegressionHeatmapResponseItem[]> =>
  request({
    url: `/regression/${slug}/heatmap`,
    method: 'POST',
    data: payload,
    signal,
  }).then((o): IRegressionHeatmapResponseItem[] => o.data);

export const calcExpertRegression = (
  portfolioDraftSlugOrStrategyCode: string,
  data: ExpertRegressionCalcRequest,
  signal: AbortSignal,
): Promise<IExpertRegressionResponse> =>
  request({
    url: `/regression/${portfolioDraftSlugOrStrategyCode}/expert-in-the-loop`,
    method: 'POST',
    data,
    signal,
  }).then((o): IExpertRegressionResponse => o.data);

export const getExcel = async (
  format: 'excel' | 'csv',
  slug: string,
  payload: IRegressionCalcRequest,
  indexName: string,
  version?: PortfolioVersionType,
): Promise<void> => {
  const dto = await request({
    url: `/regression/${slug}/${format}`,
    method: 'POST',
    params: {
      // we are separating this out from payload as the DTO payload in api side is shared between
      // excel, calcbot and web apis, while version is only used on excel.
      version,
    },
    data: payload,
  }).then((o): FileSignedUrlDTO => o.data);

  if (!dto.signedURL) return;

  checkBlockPopup();

  await FileService.downloadWithCloudFrontUrl(dto.signedURL, format === 'excel' ? 'xlsx' : 'csv', {
    tool: RouteName.FACTOR_DECOMPOSITION,
    indexName,
  });
};

export const getPdf = async (
  slug: string,
  url: string,
  params: FactorDecompositionPdfParameters & { locale?: Locales },
  indexName: string,
): Promise<void> => {
  const dto = await request({
    url: `/regression/${slug}/pdf`,
    method: 'post',
    data: {
      url,
      params: {
        ...params,
        locale: params.locale === undefined ? Locales.ENGLISH : params.locale,
      },
      options: defaultPdfOptions,
    },
  }).then((o): FileSignedUrlDTO => o.data);

  if (!dto.signedURL) return;

  checkBlockPopup();

  await FileService.downloadWithCloudFrontUrl(dto.signedURL, 'pdf', {
    tool: RouteName.FACTOR_DECOMPOSITION,
    indexName,
    date: params.endDate,
  });
};
